import { useState } from "react"
import { useForm } from "react-hook-form"
import { EyeIcon, EyeOffIcon, Loader2 } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import Input from "../../core-ui/Input"
import AuthLayout from "../../components/AuthLayout"
import API from "../../utils/apiClient"
import { useUser } from "../../utils/UserProvider"
import { useToast } from "../../utils/ToastProvider"

const Login = () => {
  const navigate = useNavigate()
  const { addToast } = useToast()
  const { user } = useUser()

  if (user) {
    navigate("/app")
  }

  const [showPassword, setShowPassword] = useState(false)
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    defaultValues: {
      email: "",
      password: "",
    }
  })

  const onSubmitHandler = async (data) => {
    try {
      const res = await API.post("/user/password-login", {
        email: data.email,
        password: data.password
      })
      if (res.data.status === "success") {
        localStorage.setItem("user_token", res.data.data.token)
        addToast({
          title: "Success!",
          description: "Logged in successfully",
          type: "error"
        })
        navigate("/app")
      } else {
        addToast({
          title: "Incorrect Info!",
          description: "Incorrect email or password",
          type: "error",
        })
      }
    } catch (LoginError) {
      addToast({
        title: "Error!",
        description: "Unexpected error occured. Try Again.",
        type: "error"
      })
    }
  }

  return (
    <AuthLayout>
      {/* Heading */}
      <h1 className="text-2xl font-semibold text-center text-gray-800 mb-2">
        Welcome Back!
      </h1>
      <p className="text-center text-gray-500 mb-8">
        Enter your email and password to login
      </p>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmitHandler)} className="space-y-6">
        {/* Email Input */}
        <div className="relative">
          <Input
            type="email"
            name="email"
            placeholder="Email"
            required={true}
            register={register}
          />
          {errors.email && <p className="mt-2 text-secondary">Email is required</p>}
        </div>

        {/* Password Input */}
        <div className="relative">
          <Input
            type={showPassword ? "text" : "password"}
            name="password"
            placeholder="Password"
            required={true}
            register={register}
          />
          {errors.password && <p className="mt-2 text-secondary">Password is required</p>}
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2"
          >
            {showPassword ? (
              <EyeOffIcon className="w-5 h-5 text-gray-400" />
            ) : (
              <EyeIcon className="w-5 h-5 text-gray-400" />
            )}
          </button>
        </div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex gap-2 justify-center items-center w-full bg-primary text-white py-3 rounded-lg hover:bg-primaryDark transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium"
        >
          {isSubmitting && <Loader2 className='md:mr-2 mr-1 h-4 w-4 animate-spin' />}
          Login
        </button>

        {/* Forgot Password */}
        <div className="text-center">
          <Link to="/forgot-password" className="text-primary hover:text-primaryDark font-medium">
            Forgot Password
          </Link>
        </div>
      </form>

      {/* Sign Up Link */}
      <p className="mt-8 text-center text-gray-500 text-sm">
        Don't have account yet?{" "}
        <Link to="/signup" className="text-primary hover:text-primaryDark font-medium">
          Sign Up
        </Link>
      </p>
    </AuthLayout>
  )
}


export default Login
